/**
 * Digit grouping for display. Admin figures are produced by the `amount` /
 * `balanceAmount` / `tradeAmount` / `precision` pipes, which all end in
 * `toFixed(...)` — grouping is layered on top of that output rather than baked
 * into those pipes, so precision stays entirely the caller's decision.
 */

/** Thousands separator used across the admin panel. */
export const GROUP_SEPARATOR = ',';

/**
 * Inserts thousands separators into an already-formatted decimal string. The
 * fractional part is passed through verbatim, so the caller's precision — and
 * its trailing zeros — survive untouched.
 *
 * Anything that is not a plain decimal (the '-', '' and 'NaN' placeholders the
 * amount pipes emit for missing data) is returned unchanged.
 */
export function groupDigits(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';

  const text = String(value).trim();
  const match = /^(-?)(\d+)(\.\d+)?$/.exec(text);
  if (!match) return String(value);

  const [, sign, whole, fraction] = match;
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
  return `${sign}${grouped}${fraction ?? ''}`;
}

/**
 * Inverse of {@link groupDigits}: drops a comma only where it sits between a
 * digit and exactly three further digits, so grouped figures paste as numbers
 * while prose commas, ids and lists survive byte-for-byte.
 */
export function stripDigitGrouping(text: string): string {
  return text.replace(/(?<=\d),(?=\d{3}(?!\d))/g, '');
}
